/**
 * UPT-2079 L14a (web §5.1) — vue du RÉSUMÉ DE STYLE d'une source (inventaire v2) : pastilles des fonds, textes, accents et bordures avec leur part, cartes
 * (nombre, rayon, ombre, bordure, verre, marge), polices de titres et de texte. Tout est rendu en texte et en pastilles de couleur DONNÉES (`swatchStyle`) ;
 * les couleurs et les familles sont en `dir="ltr"`.
 */
import { useIntl } from '@/react-intl';
import { swatchStyle } from '@/uptk/core/theme/module_theme';
import type { PrototypeColorWeight, PrototypeFontShare, PrototypeStyleSummary } from '../data/contracts';

export interface PrototypeStyleViewProps {
  style: PrototypeStyleSummary | null | undefined;
}

// i18n Rule 1 : glyphes techniques (séparateurs, symbole de pourcentage), pas du texte utilisateur — hissés au niveau du module.
const DOT = ' · ';
const percent = (share: number): string => `${Math.round(share * 1000) / 10} %`;

function Swatches({ title, entries }: { title: string; entries: PrototypeColorWeight[] }) {
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <h5 className="text-xs font-medium text-portal-ink-muted">{title}</h5>
      <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
        {entries.map((entry) => (
          <li key={entry.color} className="flex items-center gap-1 text-xs text-portal-ink">
            <span aria-hidden="true" className="inline-block size-5 rounded border border-portal-border" style={swatchStyle(entry.color)} />
            <code dir="ltr">{entry.color}</code>
            <span className="text-portal-ink-muted">{percent(entry.weight)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Fonts({ title, entries }: { title: string; entries: PrototypeFontShare[] }) {
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <h5 className="text-xs font-medium text-portal-ink-muted">{title}</h5>
      <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-xs text-portal-ink">
        {entries.map((entry) => (
          <li key={`${entry.family}-${entry.weight}`}>
            <code dir="ltr">{entry.family}</code>
            {DOT}
            <span dir="ltr">{entry.weight}</span>
            {DOT}
            {percent(entry.share)}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function PrototypeStyleView({ style }: PrototypeStyleViewProps) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  if (!style) {
    return <p className="text-sm text-portal-ink-muted">{t('moduleStudio.prototype.style.none')}</p>;
  }
  const { cards } = style;
  return (
    <section aria-label={t('moduleStudio.prototype.style.title')} className="flex flex-col gap-3 rounded-md border border-portal-border bg-portal-surface p-4">
      <h4 className="text-sm font-semibold text-portal-ink">
        {t('moduleStudio.prototype.style.title')}
        {DOT}
        <span className="font-normal text-portal-ink-muted">
          {style.scheme === 'dark' ? t('moduleStudio.prototype.style.schemeDark') : t('moduleStudio.prototype.style.schemeUnknown')}
        </span>
      </h4>
      <Swatches title={t('moduleStudio.prototype.style.backgrounds')} entries={style.backgrounds} />
      <Swatches title={t('moduleStudio.prototype.style.texts')} entries={style.texts} />
      <Swatches title={t('moduleStudio.prototype.style.accents')} entries={style.accents} />
      <Swatches title={t('moduleStudio.prototype.style.borders')} entries={style.borders} />
      <p className="text-xs text-portal-ink">
        {t('moduleStudio.prototype.style.cards', {
          count: cards.count,
          radius: cards.radiusPx === null ? '—' : `${cards.radiusPx} px`,
          shadow: percent(cards.shadowShare),
          bordered: percent(cards.borderedShare),
          glass: percent(cards.translucentShare),
        })}
      </p>
      <Fonts title={t('moduleStudio.prototype.style.fontsHeading')} entries={style.fonts.heading} />
      <Fonts title={t('moduleStudio.prototype.style.fontsBody')} entries={style.fonts.body} />
    </section>
  );
}
